// Review pager: a scroll-snap track with prev/next arrows and page dots.
// Arrows scroll by one visible page; dots are built from the visible card count and follow the scroll position.
class SReviewPager extends HTMLElement {
  #ac;
  #track;
  #dots;
  #pages = 1;

  connectedCallback() {
    this.#track = this.querySelector('[data-pager-track]');
    this.#dots = this.querySelector('[data-pager-dots]');
    if (!this.#track) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.querySelector('[data-pager-prev]')?.addEventListener('click', () => this.#go(this.#current() - 1), opts);
    this.querySelector('[data-pager-next]')?.addEventListener('click', () => this.#go(this.#current() + 1), opts);
    this.#track.addEventListener('scroll', () => this.#sync(), { ...opts, passive: true });
    new ResizeObserver(() => this.#build()).observe(this.#track);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #perPage() {
    const card = this.#track.querySelector('li');
    if (!card) return 1;
    const gap = parseFloat(getComputedStyle(card.parentElement).columnGap) || 0;
    return Math.max(1, Math.round((this.#track.clientWidth + gap) / (card.offsetWidth + gap)));
  }

  #build() {
    const cards = this.#track.querySelectorAll('li').length;
    this.#pages = Math.max(1, Math.ceil(cards / this.#perPage()));
    if (!this.#dots) return;
    const label = this.#dots.dataset.label || '';
    this.#dots.replaceChildren(
      ...Array.from({ length: this.#pages }, (_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'h-2 w-2 rounded-full bg-white/20 motion-safe:transition-[width,background-color] motion-safe:duration-300 aria-[current=true]:w-7 aria-[current=true]:bg-[#FFE43C] cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';
        b.setAttribute('aria-label', `${label} ${i + 1}`);
        b.addEventListener('click', () => this.#go(i));
        return b;
      })
    );
    this.#sync();
  }

  #current() {
    const max = this.#track.scrollWidth - this.#track.clientWidth;
    if (max <= 0) return 0;
    return Math.round((this.#track.scrollLeft / max) * (this.#pages - 1));
  }

  #go(page) {
    const count = this.#pages;
    const target = ((page % count) + count) % count;
    const max = this.#track.scrollWidth - this.#track.clientWidth;
    const left = count > 1 ? (max * target) / (count - 1) : 0;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
  }

  #sync() {
    const current = this.#current();
    this.#dots?.querySelectorAll('button').forEach((b, i) => b.setAttribute('aria-current', String(i === current)));
  }
}

if (!customElements.get('s-review-pager')) customElements.define('s-review-pager', SReviewPager);
